import { type KeyboardEvent, useRef } from 'react';
import styles from './Tabs.module.css';

/** §5 offers two renderings of the same control: inline text tabs, or a segmented pill. */
export const TABS_VARIANTS = ['underline', 'segmented'] as const;
export type TabsVariant = (typeof TABS_VARIANTS)[number];

export interface TabItem {
    readonly id: string;
    /** Sentence case, per §9 — "List", "Board", "Calendar". */
    readonly label: string;
    /** The `id` of the panel this tab controls, where one exists. */
    readonly controls?: string;
}

export interface TabsProps {
    /** Names the tab set for a screen reader — "Deal views", not "Tabs". */
    readonly label: string;
    readonly tabs: readonly TabItem[];
    readonly activeId: string;
    readonly onChange: (id: string) => void;
    readonly variant?: TabsVariant;
    readonly 'data-testid'?: string;
}

/**
 * §5's Tabs / segmented control.
 *
 * Two things here are the ARIA tabs pattern rather than choices, and both are the reason this is a
 * component instead of a row of buttons. **Roving tabindex:** exactly one tab is in the tab order,
 * so Tab moves past the whole set to the panel rather than through every view name — with four
 * tabs the naive version costs a keyboard user four keystrokes to get anywhere. **Arrow keys move
 * between tabs**, with Home/End for the ends, and selection follows focus, which is the automatic-
 * activation form the pattern specifies for cheap panel switches like these.
 *
 * Selection is controlled by the caller: this component owns keyboard behaviour and appearance, not
 * which view is open. That keeps it usable from a route param, which is how a "List / Board /
 * Calendar" switch is normally driven.
 */
export function Tabs({
    label,
    tabs,
    activeId,
    onChange,
    variant = 'underline',
    'data-testid': testId,
}: TabsProps) {
    // Keyed by tab id rather than by index: an index-keyed map goes stale the moment the caller
    // reorders or filters the tabs, and it would go stale silently — focus would simply land on the
    // wrong tab.
    const tabRefs = useRef(new Map<string, HTMLButtonElement>());

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        const currentIndex = tabs.findIndex((tab) => tab.id === activeId);
        if (currentIndex < 0) {
            return;
        }

        let nextIndex: number;
        switch (event.key) {
            case 'ArrowRight':
                nextIndex = (currentIndex + 1) % tabs.length;
                break;
            case 'ArrowLeft':
                // `+ tabs.length` before the modulo: JavaScript's % keeps the sign of the left
                // operand, so the bare form returns -1 at the first tab instead of wrapping.
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }

        const next = tabs[nextIndex];
        if (next === undefined) {
            return;
        }
        // Only after a key we handle: preventing default on every keydown would swallow the
        // browser's own shortcuts while focus happens to sit on a tab.
        event.preventDefault();
        onChange(next.id);
        tabRefs.current.get(next.id)?.focus();
    }

    return (
        // The keydown lives on the tablist rather than on each tab because arrow-key traversal is
        // a property of the SET — a handler per tab would have to know about its siblings.
        <div
            aria-label={label}
            className={`${styles.tablist} ${styles[variant]}`}
            data-testid={testId}
            onKeyDown={handleKeyDown}
            role="tablist"
        >
            {tabs.map((tab) => (
                <button
                    aria-controls={tab.controls}
                    aria-selected={tab.id === activeId}
                    className={styles.tab}
                    data-testid={testId === undefined ? undefined : `${testId}-tab-${tab.id}`}
                    key={tab.id}
                    onClick={() => onChange(tab.id)}
                    ref={(element) => {
                        if (element === null) {
                            tabRefs.current.delete(tab.id);
                        } else {
                            tabRefs.current.set(tab.id, element);
                        }
                    }}
                    role="tab"
                    // The roving part: the inactive tabs leave the tab order but stay reachable by
                    // arrow key, which is what makes Tab skip the set rather than walk it.
                    tabIndex={tab.id === activeId ? 0 : -1}
                    type="button"
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );
}
